/**
 * AllocationTable - Reusable table for displaying allocation breakdowns
 * Shows current vs target allocations with drift indicators
 */

import React from 'react';
import { AlertTriangle, CheckCircle, TrendingUp, TrendingDown } from 'lucide-react';
import TrendIndicator from '../atoms/TrendIndicator';
import type { AllocationRow } from '../index';

export interface AllocationTableProps {
  data: AllocationRow[];
  title?: string;
  showTarget?: boolean;
  showDrift?: boolean;
  showValue?: boolean;
  showActions?: boolean;
  showStatus?: boolean;
  currency?: string;
  className?: string;
  onActionClick?: (item: AllocationRow, action: string) => void;
}

const AllocationTable: React.FC<AllocationTableProps> = ({
  data,
  title,
  showTarget = true,
  showDrift = true,
  showValue = false,
  showActions = false,
  showStatus = true,
  currency = 'CHF',
  className = '',
  onActionClick
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'optimal':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'critical':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  /**
   * TASK-868: the ACTION column rendered three `<button>`s and `onActionClick` was declared and
   * never called, so every one of them was a control that looked clickable and did nothing —
   * on the table that had just labelled the row CRITICAL.
   *
   * Only `Decrease` is a real action, and only when a handler is supplied: it opens a sized
   * rebalancing proposal for that slot. `Increase` would need a buy leg — choosing an
   * instrument to buy is the fit/swap engine's job, and naming one this table cannot justify is
   * TASK-870 — and `Maintain` is not an action at all. Both now render as what they are: a
   * label saying what the row needs. A word that does nothing is honest; a button that does
   * nothing is not.
   */
  const getActionButton = (row: AllocationRow) => {
    const action = row.action;
    if (action === 'decrease' && onActionClick) {
      return (
        <button
          type="button"
          onClick={() => onActionClick(row, 'decrease')}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-400"
          aria-label={`Build a rebalancing proposal to reduce ${row.name || row.category}`}
        >
          <TrendingDown className="w-3 h-3" />
          Decrease
        </button>
      );
    }

    switch (action) {
      case 'increase':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700">
            <TrendingUp className="w-3 h-3" />
            Increase
          </span>
        );
      case 'decrease':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700">
            <TrendingDown className="w-3 h-3" />
            Decrease
          </span>
        );
      case 'maintain':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500">
            <CheckCircle className="w-3 h-3" />
            Maintain
          </span>
        );
      default:
        return null;
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className={`p-4 text-center text-gray-500 ${className}`}>
        No allocation data available
      </div>
    );
  }

  return (
    <div className={className}>
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Asset
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Current %
              </th>
              {showTarget && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target %
                </th>
              )}
              {showDrift && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Drift
                </th>
              )}
              {showValue && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
              )}
              {showStatus && (
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              )}
              {showActions && (
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {row.name || row.category}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                  {(row.current !== undefined ? row.current : row.percentage).toFixed(2)}%
                </td>
                {showTarget && (
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                    {row.target?.toFixed(2) || '-'}%
                  </td>
                )}
                {showDrift && (
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                    {(row.drift !== undefined || row.difference !== undefined) ? (
                      <TrendIndicator
                        value={row.drift !== undefined ? row.drift : (row.difference || 0)}
                        showIcon={true}
                        showValue={true}
                        suffix="%"
                        colorScheme="inverse"
                      />
                    ) : (
                      '-'
                    )}
                  </td>
                )}
                {showValue && (
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                    {row.value ? formatCurrency(row.value) : '-'}
                  </td>
                )}
                {showStatus && (
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    {getStatusIcon(row.status)}
                  </td>
                )}
                {showActions && (
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    {/* TASK-868: this cell used to wrap its contents in a `<div onClick>` that
                        fired for EVERY row, so clicking the inert `Maintain` label invoked the
                        handler for a slot the table had just called on-target — and clicking
                        the real `Decrease` button fired it twice, once from the button and
                        once from the bubble. The control is the button; the cell is not. */}
                    {getActionButton(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllocationTable;